import { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { MessageSquareText, Star } from "lucide-react";
import { fmtMYT } from "@/lib/myt";
import api from "@/lib/api";
import MemberAvatar from "@/components/MemberAvatar";

export function Stars({ value, size = 14, testId }) {
  return (
    <span className="inline-flex items-center gap-0.5" data-testid={testId} aria-label={`${value} out of 5 stars`}>
      {[1, 2, 3, 4, 5].map((n) => (
        <Star key={n} style={{ width: size, height: size }} className={n <= value ? "text-[#F5C542] fill-[#F5C542]" : "text-[#3a3d4d]"} />
      ))}
    </span>
  );
}

function FeedbackCard({ f, highlight }) {
  return (
    <article className={`card-lux rounded-2xl p-4 sm:p-5 space-y-3 ${highlight ? "border-[#D4AF37]/60 shadow-[0_0_24px_rgba(212,175,55,0.15)]" : ""}`} data-testid={`feedback-card-${f.id}`}>
      <div className="flex items-start gap-3">
        <MemberAvatar avatarUrl={f.avatar_url} size={40} alt={f.member_name} testId={`feedback-avatar-${f.id}`} />
        <div className="flex-1 min-w-0">
          <p className="text-sm font-semibold text-white truncate" data-testid={`feedback-name-${f.id}`}>{f.member_name}</p>
          <p className="text-xs text-[#64748B]">{fmtMYT(f.created_at, false)}</p>
        </div>
        <div className="flex flex-col items-end gap-1.5 shrink-0">
          <Stars value={f.rating} testId={`feedback-stars-${f.id}`} />
          <span className="text-[9px] font-mono tracking-widest uppercase rounded-full border border-[#D4AF37]/40 text-[#D4AF37] px-2 py-0.5" data-testid={`feedback-category-${f.id}`}>{f.category.replace("_", " ")}</span>
        </div>
      </div>
      {f.image_url && (
        <img src={f.image_url} alt="" loading="lazy" className="w-full max-h-[320px] object-cover rounded-xl border border-[#222530]" data-testid={`feedback-image-${f.id}`} />
      )}
      <div className="flex items-center gap-2 flex-wrap">
        <p className="text-sm text-white font-medium" data-testid={`feedback-reward-${f.id}`}>{f.reward_name}</p>
        <span className="text-[9px] font-mono tracking-widest uppercase rounded-full px-2 py-0.5 border text-emerald-400 bg-emerald-400/10 border-emerald-400/30">Delivered</span>
      </div>
      <p className="text-sm text-[#94A3B8] leading-relaxed break-words" data-testid={`feedback-comment-${f.id}`}>{f.comment}</p>
    </article>
  );
}

export default function MemberFeedback() {
  const [items, setItems] = useState([]);
  const [page, setPage] = useState(1);
  const [hasMore, setHasMore] = useState(false);
  const [state, setState] = useState("loading"); // loading | ready | error | more
  const [params] = useSearchParams();
  const highlight = params.get("highlight");
  const [category, setCategory] = useState("");

  const load = (p, cat = category) => {
    setState(p === 1 ? "loading" : "more");
    api.get(`/member/feedback?page=${p}&limit=10${cat ? `&category=${cat}` : ""}`)
      .then((res) => { setItems((prev) => (p === 1 ? res.data.items : [...prev, ...res.data.items])); setHasMore(res.data.has_more); setPage(p); setState("ready"); })
      .catch(() => setState("error"));
  };
  useEffect(() => { load(1); }, []); // eslint-disable-line react-hooks/exhaustive-deps
  const pickCategory = (c) => { setCategory(c); setItems([]); load(1, c); };

  return (
    <div className="max-w-3xl mx-auto space-y-5" data-testid="member-feedback-page">
      <div>
        <h1 className="page-title">Feedback</h1>
        <p className="page-sub mt-1">Member experiences</p>
        <div className="flex gap-2 mt-3" data-testid="feedback-filters">
          {[["", "All"], ["TRADE_REDEEM", "Trade Redeem"], ["LUCKY_DRAW", "Lucky Draw"]].map(([v, l]) => (
            <button key={v || "all"} onClick={() => pickCategory(v)} aria-pressed={category === v} data-testid={`feedback-filter-${v ? v.toLowerCase() : "all"}`}
              className={`rounded-full px-3 py-1.5 text-[11px] font-mono tracking-widest uppercase border ${category === v ? "border-[#D4AF37] bg-[#D4AF37]/15 text-[#F5D77A]" : "border-[#2a2d38] text-[#94A3B8] hover:text-white"}`}>
              {l}
            </button>
          ))}
        </div>
      </div>
      {state === "loading" && <p className="text-sm text-[#64748B]" data-testid="feedback-loading">Loading…</p>}
      {state === "error" && (
        <div className="card-lux p-6 text-center" data-testid="feedback-error">
          <p className="text-sm text-red-400">Could not load feedback.</p>
          <button onClick={() => load(1)} className="btn-outline-gold mt-3 px-4 py-2 text-xs" data-testid="feedback-retry">Retry</button>
        </div>
      )}
      {state !== "loading" && state !== "error" && items.length === 0 && (
        <div className="card-lux p-10 text-center" data-testid="feedback-empty">
          <MessageSquareText className="h-8 w-8 text-[#D4AF37]/40 mx-auto mb-3" />
          <p className="text-sm text-[#64748B]">{category ? "No feedback in this category yet." : "No feedback yet. Feedback can be shared from a delivered reward in My Redemption Requests."}</p>
        </div>
      )}
      <div className="space-y-4" data-testid="feedback-list">
        {items.map((f) => <FeedbackCard key={f.id} f={f} highlight={highlight === f.id} />)}
      </div>
      {hasMore && (
        <button onClick={() => load(page + 1)} disabled={state === "more"} className="btn-outline-gold w-full py-2.5 text-sm" data-testid="feedback-load-more">
          {state === "more" ? "Loading…" : "Load more"}
        </button>
      )}
    </div>
  );
}
